'use client';

import { AnimatePresence, motion } from 'motion/react';

import { useParams } from 'next/navigation';
import { createPortal } from 'react-dom';
import { withBodyScrollLock } from '~/hocs';
import { useIsClient, useMediaQuery } from '~/hooks';
import { Typography, Logo } from '@bgzt/ui';
import { CONTENTS_AREA_ELEMENT_ID } from '../BasicLayout';
import { Spacing } from '../Spacing';
import styles from './AppDownloadBottomSheet.module.css';
import { createFullWebViewLink } from '~/apis/short-link';

type Props = {
  isOpen?: boolean;
  onClose: () => void;
};

export const AppDownloadBottomSheet = ({ isOpen, ...props }: Props) => {
  const { isServer } = useIsClient();

  if (isServer) return null;

  return createPortal(
    <AnimatePresence>{isOpen && <AppDownloadBottomSheetBase {...props} />}</AnimatePresence>,
    globalThis.window.document.getElementById(CONTENTS_AREA_ELEMENT_ID) || globalThis.window.document.body,
  );
};

const AppDownloadBottomSheetBase = withBodyScrollLock(({ onClose, ...props }: Props) => {
  const isPC = useMediaQuery('(min-width: 968px)');
  const params = useParams();

  const handleAppLinkWithAuth = async () => {
    if (!params.pid) return;
    try {
      const url = `${process.env.NEXT_PUBLIC_DOMAIN}/products/${params.pid}`;
      const response = await createFullWebViewLink({
        url,
      });
      window.location.href = response.data.shortLink;
    } catch (e) {
      console.log('error', e);
    }
  };

  const handleAppLinkWithoutAuth = async () => {
    const oneLink = 'https://go.bgzt.link/g2iy14';
    window.location.href = oneLink;
  };

  const handleClickCTA = async () => {
    if (!params?.pid) {
      await handleAppLinkWithoutAuth();
    } else {
      await handleAppLinkWithAuth();
    }
  };

  return (
    <div
      className={styles.wrapper}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <div className={styles.dim} onClick={onClose} />

      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.3 }}
        className={styles.container}
        {...props}
      >
        <Logo.AppIcon size={95} alt="번개장터 로고" />

        <Spacing height={32} />

        <Typography variant="T3" color="gray-900">
          앱으로 더 쉽고 빠르게
          <br /> 번개장터를 이용해보세요!
        </Typography>

        {isPC && (
          <>
            <Spacing height={15} />

            <Typography variant="L5" color="gray-600">
              좌측 QR코드를 스캔해서 앱을 다운받으세요.
            </Typography>
          </>
        )}

        <Spacing height={30} />

        <button type="button" className={styles.cta} onClick={isPC ? onClose : handleClickCTA}>
          <Typography variant="L4" color="gray">
            {isPC ? '확인했어요' : '앱으로 보기'}
          </Typography>
        </button>

        {!isPC && (
          <>
            <Spacing height={18} />

            <Typography variant="B3" color="gray-400" onClick={onClose}>
              괜찮아요, 모바일웹으로 볼게요
            </Typography>
          </>
        )}
      </motion.div>
    </div>
  );
});
